import { Box, Card, FormControl, FormControlLabel, Grid, IconButton, Switch, TextField } from "@mui/material";
import { P } from "theme/typography";
import { displayDateAndTimeCompact } from "Backend";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectLensEditorLens,
  setCaseInsightsEnabled,
  setHidden,
  updateAnalyticsExclude,
  updateSectionBasedReviewEnabled,
} from "redux/slices/lensEditor";
import { useSnackbar } from "notistack";
import { ChangeEvent, useState } from "react";
import { Edit } from "@mui/icons-material";
import LensInfoEditDialog from "routes/lens-editor/settings/LensInfoEditDialog";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";

export const LensInfoSection = () => {
  const lensEditorLens = useAppSelector(selectLensEditorLens);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [showEditInfoDialog, setShowEditInfoDialog] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();

  const toggleHidden = async (evt) => {
    try {
      const hidden = !evt.target.checked;
      await dispatch(setHidden({ lensId: lensEditorLens.id, hidden })).unwrap();
      enqueueSnackbar(`Lens ${hidden ? "hidden" : "visible"}`, { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Unable to update hidden", { variant: "error" });
    }
  };
  const toggleCaseInsightsEnabled = async (evt) => {
    try {
      const caseInsightsEnabled = evt.target.checked;
      await dispatch(setCaseInsightsEnabled({ lensId: lensEditorLens.id, caseInsightsEnabled })).unwrap();
      enqueueSnackbar(`Case Insights ${caseInsightsEnabled ? "enabled" : "disabled"} for lens`, { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Unable to update case insights enabled", { variant: "error" });
    }
  };
  const handleToggleAnalyticsExclude = (event: ChangeEvent<HTMLInputElement>) => {
    const analyticsExclude = event.target.checked;
    const message = analyticsExclude ? "Lens is now excluded from analytics" : "Lens no longer excluded from analytics";

    snackpatch(updateAnalyticsExclude({ lensId: lensEditorLens.id, analyticsExclude: analyticsExclude }), {
      successMessage: message,
      errorMessage: "Unable to update analytics status",
    });
  };

  const handleToggleSectionBasedReviewEnabled = (event: ChangeEvent<HTMLInputElement>) => {
    const sectionBasedReviewEnabled = event.target.checked;
    const message = sectionBasedReviewEnabled
      ? "Section based enabled for this lens"
      : "Section based review disabled for this lens";

    snackpatch(
      updateSectionBasedReviewEnabled({
        lensId: lensEditorLens.id,
        sectionBasedReviewEnabled: sectionBasedReviewEnabled,
      }),
      {
        successMessage: message,
        errorMessage: "Unable to update section based review status",
      }
    );
  };

  return (
    <Box sx={{ my: 1, mb: 2 }}>
      {showEditInfoDialog && <LensInfoEditDialog onClose={() => setShowEditInfoDialog(false)} />}

      <Card sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
            }}>
            <P variant="xs">Lens ID: {lensEditorLens.id}</P>
            <P variant="xs">Lens Version: {lensEditorLens.version}</P>
            <P variant="xs">Created on: {displayDateAndTimeCompact(lensEditorLens.createdAt)}</P>
          </Box>
          <IconButton onClick={() => setShowEditInfoDialog(true)}>
            <Edit />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField label="Lens Name" value={lensEditorLens.name} disabled fullWidth name="name" />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <TextField label="External ID" value={lensEditorLens.externalId || ""} fullWidth disabled />
            </FormControl>
          </Grid>
          <Grid item xs={8}>
            <FormControl fullWidth>
              <TextField label="Lens Description" value={lensEditorLens.description} disabled />
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <TextField label="Litigation Name" value={lensEditorLens.litigationName} disabled />
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField label="Rank" value={lensEditorLens.rank} disabled fullWidth />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              label="Lens Visible"
              control={<Switch checked={!lensEditorLens.hidden} onChange={toggleHidden} />}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              label="Case Insights"
              control={
                <Switch
                  checked={lensEditorLens.calcsEnabled}
                  onChange={toggleCaseInsightsEnabled}
                  name="caseInsightsEnabled"
                />
              }
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              id="analytics-exclude"
              control={
                <Switch checked={Boolean(lensEditorLens.analyticsExclude)} onChange={handleToggleAnalyticsExclude} />
              }
              label="Exclude from Analytics"
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              id="enable-sbr"
              control={
                <Switch
                  checked={Boolean(lensEditorLens.sectionBasedReviewEnabled)}
                  onChange={handleToggleSectionBasedReviewEnabled}
                />
              }
              label="Section Based Review"
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};
